import type { NotificationEndpointIdentity } from '../../settings/model.js';
import type { NinjaBackupInventoryRow, SettingsPageModel } from './model.js';

export interface GroupAssignmentEditorState {
  groupId: string | null;
  search: string;
  selectedEndpointKeys: ReadonlySet<string>;
  persistedEndpointKeys: ReadonlySet<string>;
}

export function createGroupAssignmentEditorState(): GroupAssignmentEditorState {
  return {
    groupId: null,
    search: '',
    selectedEndpointKeys: new Set(),
    persistedEndpointKeys: new Set(),
  };
}

export function loadGroupAssignment(
  model: SettingsPageModel,
  groupId: string,
  search = '',
): GroupAssignmentEditorState {
  const groupExists = model.additionalNotificationGroups.some((group) => group.groupId === groupId);
  if (!groupExists) return createGroupAssignmentEditorState();
  const persistedEndpointKeys = new Set(
    model.deviceGroupAssignments
      .filter((assignment) => assignment.groupIds.includes(groupId))
      .map(endpointKey),
  );
  return {
    groupId,
    search,
    selectedEndpointKeys: new Set(persistedEndpointKeys),
    persistedEndpointKeys,
  };
}

export function withAssignmentSearch(
  state: GroupAssignmentEditorState,
  search: string,
): GroupAssignmentEditorState {
  return { ...state, search };
}

export function filteredAssignmentEndpoints(
  model: SettingsPageModel,
  search: string,
): NinjaBackupInventoryRow[] {
  const query = search.trim().toLocaleLowerCase();
  return [...model.inventory]
    .filter((endpoint) => query.length === 0
      || endpoint.deviceName.toLocaleLowerCase().includes(query)
      || endpoint.organizationName.toLocaleLowerCase().includes(query))
    .sort((left, right) =>
      left.organizationName.localeCompare(right.organizationName, undefined, { sensitivity: 'base' })
      || left.deviceName.localeCompare(right.deviceName, undefined, { sensitivity: 'base' })
      || left.organizationId - right.organizationId
      || left.deviceId - right.deviceId);
}

export function toggleAssignmentEndpoint(
  state: GroupAssignmentEditorState,
  key: string,
  selected: boolean,
): GroupAssignmentEditorState {
  const selectedEndpointKeys = new Set(state.selectedEndpointKeys);
  if (selected) selectedEndpointKeys.add(key);
  else selectedEndpointKeys.delete(key);
  return { ...state, selectedEndpointKeys };
}

export function selectAllFilteredEndpoints(
  state: GroupAssignmentEditorState,
  endpoints: readonly NotificationEndpointIdentity[],
): GroupAssignmentEditorState {
  const selectedEndpointKeys = new Set(state.selectedEndpointKeys);
  for (const endpoint of endpoints) selectedEndpointKeys.add(endpointKey(endpoint));
  return { ...state, selectedEndpointKeys };
}

export function clearAssignmentSelection(
  state: GroupAssignmentEditorState,
): GroupAssignmentEditorState {
  return { ...state, selectedEndpointKeys: new Set() };
}

export function hasUnsavedGroupAssignmentChanges(state: GroupAssignmentEditorState): boolean {
  return !sameKeys(state.selectedEndpointKeys, state.persistedEndpointKeys);
}

export function allowGroupAssignmentSwitch(
  state: GroupAssignmentEditorState,
  confirmDiscard: () => boolean,
): boolean {
  return !hasUnsavedGroupAssignmentChanges(state) || confirmDiscard();
}

export function selectedAssignmentEndpoints(
  state: GroupAssignmentEditorState,
): NotificationEndpointIdentity[] {
  return [...state.selectedEndpointKeys]
    .map(parseEndpointKey)
    .filter((endpoint): endpoint is NotificationEndpointIdentity => endpoint !== null)
    .sort((left, right) =>
      left.organizationId - right.organizationId || left.deviceId - right.deviceId);
}

export function endpointKey(endpoint: NotificationEndpointIdentity): string {
  return `${endpoint.organizationId}:${endpoint.deviceId}`;
}

function parseEndpointKey(value: string): NotificationEndpointIdentity | null {
  const [organizationValue, deviceValue, extra] = value.split(':');
  const organizationId = Number(organizationValue);
  const deviceId = Number(deviceValue);
  return extra === undefined
    && Number.isInteger(organizationId) && organizationId > 0
    && Number.isInteger(deviceId) && deviceId > 0
    ? { organizationId, deviceId }
    : null;
}

function sameKeys(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  return left.size === right.size && [...left].every((key) => right.has(key));
}
